import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Grid,
  CircularProgress,
} from "@mui/material";
import { Timestamp } from "firebase/firestore";
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const Add = ({ onRecordAdded, editRecord }) => {
  // Use Firestore hook with the collection name "TpCtagCatalog"
  const { addData, updateData } = useFirestore("TpCtagCatalog");

  // Default blank form data
  const defaultFormData = {
    CTAN: "",
    CTAN_Name: "",
    EMIS_Code: "",
    ODE_career_field: "",
    Articulation_Number: "",
    Sinclair_Course: "",
    Sinclair_Subject_Short: "",
    Sinclair_Subject_Long: "",
    Credits: "",
    Sinclair_Course_and_Subject: "",
    ODHE_Portal_In: 0,
    ODHE_Portal_Note_01: "",
    Colleague_TCEQ_In: 0,
    Colleague_CTAN: "",
    Colleague_Start_Date: "",
    Colleague_End_Date: "",
    Colleague_Note_01: "",
    TP_Office_Note_01: "",
    OK_to_Post: 0,
    Status_A: "",
  };

  // Initialize formData for add/edit
  const [formData, setFormData] = useState(defaultFormData);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // If editRecord changes, update formData
  useEffect(() => {
    if (editRecord) {
      setFormData(editRecord);
    } else {
      // For new records, populate start and end dates with current date
      const currentDate = new Date().toISOString().split("T")[0]; // Format as YYYY-MM-DD
      setFormData({
        ...defaultFormData,
        Colleague_Start_Date: currentDate,
        Colleague_End_Date: currentDate,
      });
    }
  }, [editRecord]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      let processedValue = value;

      // Convert Credits field to number
      if (name === "Credits") {
        processedValue = value === "" ? "" : parseFloat(value) || 0;
      }

      const newData = {
        ...prevData,
        [name]: processedValue,
      };

      // Auto-calculate Sinclair_Course_and_Subject when related fields change
      // Formula: [@field:Course]+': '+[@field:Subject]
      if (name === "Sinclair_Course" || name === "Sinclair_Subject_Long") {
        const course =
          name === "Sinclair_Course" ? value : newData.Sinclair_Course || "";
        const subject =
          name === "Sinclair_Subject_Long"
            ? value
            : newData.Sinclair_Subject_Long || "";
        newData.Sinclair_Course_and_Subject =
          course && subject ? `${course}: ${subject}` : "";
      }

      // Auto-calculate Colleague_CTAN when CTAN changes
      if (name === "CTAN" && value) {
        const ctan = value.toString();
        if (ctan.length >= 4) {
          const left4 = ctan.substring(0, 4);
          const remaining = ctan.substring(4);
          newData.Colleague_CTAN = `${left4}*${remaining}`;
        } else {
          newData.Colleague_CTAN = "";
        }
      }

      return newData;
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? -1 : 0,
    }));
  };

  const handleSubmit = async () => {
    // Require CTAN field
    if (!safeFormData.CTAN || safeFormData.CTAN.trim() === "") {
      setSnackbarOpen(true);
      setLoading(false);
      alert("CTAN is required.");
      return;
    }
    setLoading(true);
    try {
      // Prepare data for submission, converting date strings to Timestamps
      const submitData = { ...formData };

      // Convert date strings to Firestore Timestamps
      if (
        submitData.Colleague_Start_Date &&
        typeof submitData.Colleague_Start_Date === "string" &&
        submitData.Colleague_Start_Date.trim() !== ""
      ) {
        const startDate = new Date(
          submitData.Colleague_Start_Date + "T12:00:00.000Z"
        );
        submitData.Colleague_Start_Date = Timestamp.fromDate(startDate);
      } else if (
        !submitData.Colleague_Start_Date ||
        submitData.Colleague_Start_Date === ""
      ) {
        submitData.Colleague_Start_Date = null;
      }

      if (
        submitData.Colleague_End_Date &&
        typeof submitData.Colleague_End_Date === "string" &&
        submitData.Colleague_End_Date.trim() !== ""
      ) {
        const endDate = new Date(
          submitData.Colleague_End_Date + "T12:00:00.000Z"
        );
        submitData.Colleague_End_Date = Timestamp.fromDate(endDate);
      } else if (
        !submitData.Colleague_End_Date ||
        submitData.Colleague_End_Date === ""
      ) {
        submitData.Colleague_End_Date = null;
      }

      if (editRecord && editRecord.id) {
        await updateData(editRecord.id, submitData); // Edit mode
      } else {
        await addData(submitData); // Add mode
      }
      setLoading(false);
      setSnackbarOpen(true);
      setFormData(defaultFormData);
      setOpen(false);
      onRecordAdded(); // Call the callback function to refresh the list
    } catch (error) {
      console.error("Error saving record:", error);
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Defensive fallback for formData
  const safeFormData = formData || defaultFormData;

  return (
    <div>
      <Button
        variant="contained"
        color="success"
        onClick={() => setOpen(true)}
        sx={{ marginBottom: "10px", color: "white" }}
      >
        Add Record
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleClose}>Add Record</ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="CTAN"
                name="CTAN"
                value={safeFormData.CTAN}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="CTAN Name"
                name="CTAN_Name"
                value={safeFormData.CTAN_Name}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="EMIS Code"
                name="EMIS_Code"
                value={safeFormData.EMIS_Code}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="ODE Career Field"
                name="ODE_career_field"
                value={safeFormData.ODE_career_field}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Articulation Number"
                name="Articulation_Number"
                value={safeFormData.Articulation_Number}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Sinclair Course"
                name="Sinclair_Course"
                value={safeFormData.Sinclair_Course}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Sinclair Subject Short"
                name="Sinclair_Subject_Short"
                value={safeFormData.Sinclair_Subject_Short}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Sinclair Subject Long"
                name="Sinclair_Subject_Long"
                value={safeFormData.Sinclair_Subject_Long}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Credits"
                name="Credits"
                type="number"
                inputProps={{
                  step: "0.1",
                  min: "0",
                }}
                value={safeFormData.Credits}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Sinclair Course and Subject"
                name="Sinclair_Course_and_Subject"
                value={safeFormData.Sinclair_Course_and_Subject}
                disabled
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#999",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="ODHE Portal Note 01"
                name="ODHE_Portal_Note_01"
                value={safeFormData.ODHE_Portal_Note_01}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Colleague CTAN"
                name="Colleague_CTAN"
                value={safeFormData.Colleague_CTAN}
                disabled
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#999",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Colleague Start Date"
                name="Colleague_Start_Date"
                type="date"
                value={safeFormData.Colleague_Start_Date || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Colleague End Date"
                name="Colleague_End_Date"
                type="date"
                value={safeFormData.Colleague_End_Date || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Colleague Note 01"
                name="Colleague_Note_01"
                value={safeFormData.Colleague_Note_01}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="TP Office Note 01"
                name="TP_Office_Note_01"
                value={safeFormData.TP_Office_Note_01}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Status A"
                name="Status_A"
                value={safeFormData.Status_A}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={safeFormData.ODHE_Portal_In === -1}
                    onChange={handleCheckboxChange}
                    name="ODHE_Portal_In"
                  />
                }
                label="ODHE Portal In"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={safeFormData.Colleague_TCEQ_In === -1}
                    onChange={handleCheckboxChange}
                    name="Colleague_TCEQ_In"
                  />
                }
                label="Colleague TCEQ In"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={safeFormData.OK_to_Post === -1}
                    onChange={handleCheckboxChange}
                    name="OK_to_Post"
                  />
                }
                label="OK to Post"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message="Record has been added"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        ContentProps={{
          sx: {
            backgroundColor: "black",
            color: "white",
          },
        }}
      />
    </div>
  );
};

export default Add;
